import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { extractTeethFromNotes } from '../../utils/teeth';
import DateLink from '../links/DateLink';
import './PatientProfile.css';

const PatientProfile = ({ patientId, language = 'he', onClose }) => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  // Note form state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    note_type: 'general',
    title: '',
    content: '',
    teeth_involved: [],
    is_pinned: false
  });

  const tabs = [
    { id: 'info', icon: '👤', label: language === 'he' ? 'פרטים' : 'Info' },
    { id: 'history', icon: '📋', label: language === 'he' ? 'היסטוריה' : 'History' },
    { id: 'dental', icon: '🦷', label: language === 'he' ? 'תרשים שיניים' : 'Dental Chart' },
    { id: 'notes', icon: '📝', label: language === 'he' ? 'הערות' : 'Notes' },
    { id: 'treatment', icon: '💊', label: language === 'he' ? 'תוכנית טיפול' : 'Treatment' },
  ];

  const noteTypes = [
    { value: 'clinical', label: language === 'he' ? 'קליני' : 'Clinical' },
    { value: 'treatment', label: language === 'he' ? 'טיפול' : 'Treatment' },
    { value: 'progress', label: language === 'he' ? 'התקדמות' : 'Progress' },
    { value: 'allergies', label: language === 'he' ? 'אלרגיות' : 'Allergies' },
    { value: 'medical_history', label: language === 'he' ? 'היסטוריה רפואית' : 'Medical History' },
    { value: 'prescription', label: language === 'he' ? 'מרשם' : 'Prescription' },
    { value: 'follow_up', label: language === 'he' ? 'מעקב' : 'Follow-up' },
    { value: 'general', label: language === 'he' ? 'כללי' : 'General' },
  ];

  const statusLabels = {
    booked: { en: 'Booked', he: 'הוזמן' },
    scheduled: { en: 'Scheduled', he: 'מתוכנן' },
    confirmed: { en: 'Confirmed', he: 'מאושר' },
    arrived: { en: 'Arrived', he: 'הגיע' },
    in_progress: { en: 'In Progress', he: 'בטיפול' },
    completed: { en: 'Completed', he: 'הושלם' },
    canceled: { en: 'Cancelled', he: 'בוטל' },
    cancelled: { en: 'Cancelled', he: 'בוטל' },
    no_show: { en: 'No Show', he: 'לא הגיע' },
    rescheduled: { en: 'Rescheduled', he: 'נקבע מחדש' }
  };

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await authFetch(`/api/patients/${patientId}/profile?language=${language}`);
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      } else {
        throw new Error('Failed to fetch profile');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(language === 'he' ? 'שגיאה בטעינת הפרופיל' : 'Error loading profile');
      // No demo fallback: this used to render a fabricated profile (fake
      // penicillin allergy, fake notes) under a REAL patient's id whenever
      // the fetch failed — invented medical data is worse than an error.
      setProfile(null);
    }

    setLoading(false);
  }, [patientId, authFetch, language]);

  useEffect(() => {
    if (patientId) {
      fetchProfile();
    }
  }, [patientId, fetchProfile]);

  // Teeth referenced by notes (structured teeth_involved + free-text
  // mentions like "שן 36") — surfaced on the dental chart so notes and
  // the tooth map stop disagreeing.
  const mentionedTeeth = useMemo(
    () => extractTeethFromNotes(profile?.notes),
    [profile?.notes]
  );


  // Save note
  const handleSaveNote = async () => {
    if (!noteForm.content.trim()) {
      alert(language === 'he' ? 'נא להזין תוכן להערה' : 'Please enter note content');
      return;
    }

    try {
      const url = editingNote
        ? `/api/patients/${patientId}/notes/${editingNote.id}`
        : `/api/patients/${patientId}/notes`;

      const method = editingNote ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(noteForm)
      });

      if (res.ok) {
        setShowNoteModal(false);
        setEditingNote(null);
        setNoteForm({ note_type: 'general', title: '', content: '', teeth_involved: [], is_pinned: false });
        fetchProfile();
      }
    } catch (err) {
      console.error('Error saving note:', err);
      // Demo mode - add to local state
      const newNote = {
        id: Date.now(),
        ...noteForm,
        created_at: new Date().toISOString()
      };
      setProfile(prev => ({
        ...prev,
        notes: editingNote
          ? prev.notes.map(n => n.id === editingNote.id ? { ...n, ...noteForm } : n)
          : [newNote, ...prev.notes]
      }));
      setShowNoteModal(false);
      setEditingNote(null);
      setNoteForm({ note_type: 'general', title: '', content: '', teeth_involved: [], is_pinned: false });
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm(language === 'he' ? 'למחוק הערה זו?' : 'Delete this note?')) {
      return;
    }

    try {
      await authFetch(`/api/patients/${patientId}/notes/${noteId}`, { method: 'DELETE' });
      fetchProfile();
    } catch (err) {
      // Demo mode
      setProfile(prev => ({
        ...prev,
        notes: prev.notes.filter(n => n.id !== noteId)
      }));
    }
  };

  // Render Info Tab
  const renderInfoTab = () => {
    if (!profile?.patient) return null;
    const p = profile.patient;

    return (
      <div className="info-tab">
        <div className="info-sections">
          {/* Personal Information */}
          <div className="info-section">
            <h3>{language === 'he' ? 'פרטים אישיים' : 'Personal Information'}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{language === 'he' ? 'שם מלא' : 'Full Name'}</label>
                <span>{p.name}</span>
              </div>
              <div className="info-item">
                <label>{language === 'he' ? 'טלפון' : 'Phone'}</label>
                <span dir="ltr">{p.phone}</span>
              </div>
              <div className="info-item">
                <label>{language === 'he' ? 'אימייל' : 'Email'}</label>
                <span>{p.email || '-'}</span>
              </div>
              <div className="info-item">
                <label>{language === 'he' ? 'תאריך לידה' : 'Date of Birth'}</label>
                <span>{p.date_of_birth || '-'}</span>
              </div>
              <div className="info-item">
                <label>{language === 'he' ? 'שפה מועדפת' : 'Preferred Language'}</label>
                <span>{p.preferred_language === 'he' ? 'עברית' : 'English'}</span>
              </div>
              <div className="info-item">
                <label>{language === 'he' ? 'העדפת תקשורת' : 'Communication'}</label>
                <span>{p.communication_preference?.toUpperCase() || 'SMS'}</span>
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="info-section">
            <h3>{language === 'he' ? 'מידע רפואי' : 'Medical Information'}</h3>
            <div className="info-grid">
              <div className="info-item full-width">
                <label>{language === 'he' ? 'אלרגיות' : 'Allergies'}</label>
                <div className="tag-list">
                  {p.medical_history?.allergies?.length > 0 ? (
                    p.medical_history.allergies.map((allergy, i) => (
                      <span key={i} className="tag danger">{allergy}</span>
                    ))
                  ) : (
                    <span className="no-data">{language === 'he' ? 'לא ידוע' : 'None reported'}</span>
                  )}
                </div>
              </div>
              <div className="info-item full-width">
                <label>{language === 'he' ? 'מצבים רפואיים' : 'Medical Conditions'}</label>
                <div className="tag-list">
                  {p.medical_history?.conditions?.length > 0 ? (
                    p.medical_history.conditions.map((condition, i) => (
                      <span key={i} className="tag warning">{condition}</span>
                    ))
                  ) : (
                    <span className="no-data">{language === 'he' ? 'לא ידוע' : 'None reported'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Insurance */}
          <div className="info-section">
            <h3>{language === 'he' ? 'ביטוח' : 'Insurance'}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{language === 'he' ? 'חברת ביטוח' : 'Provider'}</label>
                <span>{p.insurance_provider || '-'}</span>
              </div>
              <div className="info-item">
                <label>{language === 'he' ? 'מספר פוליסה' : 'Policy Number'}</label>
                <span>{p.insurance_policy_number || '-'}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="info-section">
            <h3>{language === 'he' ? 'סטטיסטיקה' : 'Statistics'}</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-value">{profile.stats.total_appointments}</span>
                <span className="stat-label">{language === 'he' ? 'סה"כ תורים' : 'Total Appointments'}</span>
              </div>
              <div className="stat-card success">
                <span className="stat-value">{profile.stats.completed_appointments}</span>
                <span className="stat-label">{language === 'he' ? 'הושלמו' : 'Completed'}</span>
              </div>
              <div className="stat-card warning">
                <span className="stat-value">{profile.stats.upcoming_appointments}</span>
                <span className="stat-label">{language === 'he' ? 'עתידיים' : 'Upcoming'}</span>
              </div>
              <div className="stat-card danger">
                <span className="stat-value">{profile.stats.no_shows}</span>
                <span className="stat-label">{language === 'he' ? 'לא הגיע' : 'No Shows'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render History Tab
  const renderHistoryTab = () => (
    <div className="history-tab">
      <h3>{language === 'he' ? 'היסטוריית תורים' : 'Appointment History'}</h3>

      {profile?.appointments?.length > 0 ? (
        <div className="appointments-list">
          {profile.appointments.map(apt => (
            <div key={apt.id} className={`appointment-card ${apt.status}`}>
              <div className="apt-date">
                <span className="date"><DateLink date={apt.date} title={language === 'he' ? 'פתח ביומן' : 'Open in calendar'} /></span>
                <span className="time">{apt.time}</span>
              </div>
              <div className="apt-details">
                <span className="service">{apt.service}</span>
                <span className="provider">{apt.provider}</span>
              </div>
              <div className="apt-status">
                <span className={`status-badge ${apt.status}`}>
                  {statusLabels[apt.status]?.[language] || apt.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-data">{language === 'he' ? 'אין תורים' : 'No appointments'}</p>
      )}

      {profile?.visit_summaries?.length > 0 && (
        <>
          <h3 style={{ marginTop: '24px' }}>{language === 'he' ? 'סיכומי ביקור' : 'Visit Summaries'}</h3>
          <div className="summaries-list">
            {profile.visit_summaries.map(vs => (
              <div key={vs.id} className="summary-card">
                <p className="summary-text">{vs.summary}</p>
                <p className="summary-date">{new Date(vs.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // Render Dental Chart Tab
  const renderDentalTab = () => {
    const teeth = {
      upper: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
      lower: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]
    };

    const getToothConditions = (toothNum) => {
      const dental = profile?.patient?.dental_history || {};
      return dental[toothNum] || [];
    };

    const getToothClass = (toothNum) => {
      const conditions = getToothConditions(toothNum);
      if (conditions.includes('missing')) return 'missing';
      if (conditions.includes('crown')) return 'crown';
      if (conditions.includes('filling')) return 'filling';
      if (conditions.includes('cavity')) return 'cavity';
      if (conditions.includes('root_canal')) return 'root-canal';
      return '';
    };

    const renderTooth = (tooth, isUpper) => {
      const noteRefs = mentionedTeeth.get(tooth);
      const conditionText = getToothConditions(tooth).join(', ') || (language === 'he' ? 'בריא' : 'Healthy');
      const mentionText = noteRefs
        ? (language === 'he'
            ? ` · מוזכר ב-${noteRefs.length} הערות — לחצו למעבר להערות`
            : ` · mentioned in ${noteRefs.length} note(s) — click to view notes`)
        : '';
      return (
        <div
          key={tooth}
          className={`tooth ${getToothClass(tooth)} ${noteRefs ? 'mentioned' : ''}`}
          title={`${language === 'he' ? 'שן' : 'Tooth'} ${tooth}: ${conditionText}${mentionText}`}
          onClick={noteRefs ? () => setActiveTab('notes') : undefined}
          role={noteRefs ? 'button' : undefined}
          tabIndex={noteRefs ? 0 : undefined}
          onKeyDown={noteRefs ? (e) => { if (e.key === 'Enter') setActiveTab('notes'); } : undefined}
        >
          {isUpper && <span className="tooth-number">{tooth}</span>}
          <div className="tooth-icon">🦷</div>
          {!isUpper && <span className="tooth-number">{tooth}</span>}
          {noteRefs && <span className="tooth-note-dot" aria-hidden="true">📝</span>}
        </div>
      );
    };

    return (
      <div className="dental-tab">
        <h3>{language === 'he' ? 'תרשים שיניים' : 'Dental Chart'}</h3>

        <div className="dental-chart">
          {/* Upper teeth */}
          <div className="teeth-row upper">
            <span className="row-label">{language === 'he' ? 'עליון' : 'Upper'}</span>
            {teeth.upper.map(tooth => renderTooth(tooth, true))}
          </div>

          {/* Lower teeth */}
          <div className="teeth-row lower">
            <span className="row-label">{language === 'he' ? 'תחתון' : 'Lower'}</span>
            {teeth.lower.map(tooth => renderTooth(tooth, false))}
          </div>
        </div>

        {/* Legend */}
        <div className="dental-legend">
          <div className="legend-item"><span className="legend-dot healthy"></span> {language === 'he' ? 'בריא' : 'Healthy'}</div>
          <div className="legend-item"><span className="legend-dot filling"></span> {language === 'he' ? 'סתימה' : 'Filling'}</div>
          <div className="legend-item"><span className="legend-dot crown"></span> {language === 'he' ? 'כתר' : 'Crown'}</div>
          <div className="legend-item"><span className="legend-dot cavity"></span> {language === 'he' ? 'עששת' : 'Cavity'}</div>
          <div className="legend-item"><span className="legend-dot root-canal"></span> {language === 'he' ? 'טיפול שורש' : 'Root Canal'}</div>
          <div className="legend-item"><span className="legend-dot missing"></span> {language === 'he' ? 'חסר' : 'Missing'}</div>
          <div className="legend-item"><span className="legend-note">📝</span> {language === 'he' ? 'מוזכר בהערות' : 'Mentioned in notes'}</div>
        </div>
      </div>
    );
  };

  // Render Notes Tab
  const renderNotesTab = () => (
    <div className="notes-tab">
      <div className="notes-header">
        <h3>{language === 'he' ? 'הערות קליניות' : 'Clinical Notes'}</h3>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingNote(null);
            setNoteForm({ note_type: 'general', title: '', content: '', teeth_involved: [], is_pinned: false });
            setShowNoteModal(true);
          }}
        >
          + {language === 'he' ? 'הערה חדשה' : 'New Note'}
        </button>
      </div>

      {profile?.notes?.length > 0 ? (
        <div className="notes-list">
          {profile.notes.map(note => (
            <div key={note.id} className={`note-card ${note.is_pinned ? 'pinned' : ''}`}>
              <div className="note-header">
                <span className={`note-type ${note.note_type}`}>{note.note_type}</span>
                {note.is_pinned && <span className="pin-icon">📌</span>}
                <span className="note-date">
                  {new Date(note.created_at).toLocaleDateString()}
                </span>
              </div>
              {note.title && <h4 className="note-title">{note.title}</h4>}
              <p className="note-content">{note.content}</p>
              {note.teeth_involved?.length > 0 && (
                <button
                  type="button"
                  className="note-teeth note-teeth--link"
                  onClick={() => setActiveTab('dental')}
                  title={language === 'he' ? 'הצג בתרשים השיניים' : 'Show on dental chart'}
                >
                  🦷 {note.teeth_involved.join(', ')}
                </button>
              )}
              <div className="note-actions">
                <button
                  onClick={() => {
                    setEditingNote(note);
                    setNoteForm({
                      note_type: note.note_type,
                      title: note.title || '',
                      content: note.content,
                      teeth_involved: note.teeth_involved || [],
                      is_pinned: note.is_pinned
                    });
                    setShowNoteModal(true);
                  }}
                >
                  {language === 'he' ? 'ערוך' : 'Edit'}
                </button>
                <button className="danger" onClick={() => handleDeleteNote(note.id)}>
                  {language === 'he' ? 'מחק' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-data">{language === 'he' ? 'אין הערות' : 'No notes'}</p>
      )}
    </div>
  );

  // Render Treatment Tab
  const renderTreatmentTab = () => (
    <div className="treatment-tab">
      <h3>{language === 'he' ? 'תוכניות טיפול' : 'Treatment Plans'}</h3>

      {profile?.treatment_plans?.length > 0 ? (
        <div className="treatment-list">
          {profile.treatment_plans.map(plan => (
            <div key={plan.id} className={`treatment-card ${plan.status}`}>
              <div className="treatment-header">
                <h4>{plan.title}</h4>
                <span className={`status-badge ${plan.status}`}>{plan.status}</span>
              </div>

              <div className="treatment-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${plan.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{plan.progress}%</span>
              </div>

              {plan.estimated_cost && (
                <div className="treatment-cost">
                  <span className="cost-label">{language === 'he' ? 'עלות משוערת:' : 'Estimated Cost:'}</span>
                  <span className="cost-value">₪{plan.estimated_cost.toLocaleString()}</span>
                </div>
              )}

              {plan.start_date && (
                <div className="treatment-dates">
                  <span>{language === 'he' ? 'התחלה:' : 'Start:'} {plan.start_date}</span>
                  {plan.estimated_end_date && (
                    <span>{language === 'he' ? 'סיום משוער:' : 'Est. End:'} {plan.estimated_end_date}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="no-data">{language === 'he' ? 'אין תוכניות טיפול' : 'No treatment plans'}</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="patient-profile loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="patient-profile loading">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="patient-profile">
      {/* Header */}
      <div className="profile-header">
        <div className="patient-summary">
          <div className="patient-avatar">
            {profile?.patient?.name?.charAt(0) || '?'}
          </div>
          <div className="patient-info">
            <h1>{profile?.patient?.name || 'Unknown'}</h1>
            <span className="patient-id">ID: {patientId}</span>
            <span className="patient-phone" dir="ltr">{profile?.patient?.phone}</span>
          </div>
        </div>

        {onClose && (
          <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        )}
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="profile-content">
        {activeTab === 'info' && renderInfoTab()}
        {activeTab === 'history' && renderHistoryTab()}
        {activeTab === 'dental' && renderDentalTab()}
        {activeTab === 'notes' && renderNotesTab()}
        {activeTab === 'treatment' && renderTreatmentTab()}
      </div>

      {/* Note Modal */}
      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingNote
                  ? (language === 'he' ? 'עריכת הערה' : 'Edit Note')
                  : (language === 'he' ? 'הערה חדשה' : 'New Note')
                }
              </h2>
              <button className="close-btn" onClick={() => setShowNoteModal(false)} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>{language === 'he' ? 'סוג הערה' : 'Note Type'}</label>
                <select
                  value={noteForm.note_type}
                  onChange={e => setNoteForm({ ...noteForm, note_type: e.target.value })}
                >
                  {noteTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{language === 'he' ? 'כותרת' : 'Title'}</label>
                <input
                  type="text"
                  value={noteForm.title}
                  onChange={e => setNoteForm({ ...noteForm, title: e.target.value })}
                  placeholder={language === 'he' ? 'כותרת (אופציונלי)' : 'Title (optional)'}
                />
              </div>

              <div className="form-group">
                <label>{language === 'he' ? 'תוכן' : 'Content'} *</label>
                <textarea
                  value={noteForm.content}
                  onChange={e => setNoteForm({ ...noteForm, content: e.target.value })}
                  placeholder={language === 'he' ? 'תוכן ההערה...' : 'Note content...'}
                  rows={5}
                />
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={noteForm.is_pinned}
                    onChange={e => setNoteForm({ ...noteForm, is_pinned: e.target.checked })}
                  />
                  {language === 'he' ? 'הצמד למעלה' : 'Pin to top'}
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowNoteModal(false)}>
                {language === 'he' ? 'ביטול' : 'Cancel'}
              </button>
              <button className="btn-primary" onClick={handleSaveNote}>
                {language === 'he' ? 'שמור' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientProfile;
